/**
 * Subscription API client
 * Fetches subscription status from the backend (source of truth)
 */

import { Capacitor } from '@capacitor/core'
import { CapacitorHttp } from '@capacitor/core'
import { supabase } from '@/lib/supabase'
import type { SubscriptionStatus } from '@/types/database'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.miac.app'

const FREE_VERIFICATION_LIMIT = 14

/**
 * Fetch subscription status from backend API
 * This is the source of truth for subscription state
 */
export async function fetchSubscriptionStatus(): Promise<SubscriptionStatus> {
  const { data: { session } } = await supabase.auth.getSession()
  
  if (!session) {
    // Return default free tier for unauthenticated users
    return {
      tier: 'free',
      isPremium: false,
      status: null,
      provider: null,
      expiresAt: null,
      freeVerificationsUsed: 0,
      freeVerificationsRemaining: FREE_VERIFICATION_LIMIT,
      freeVerificationsLimit: FREE_VERIFICATION_LIMIT,
      purchasedVerifications: 0,
      totalVerificationsRemaining: FREE_VERIFICATION_LIMIT,
    }
  }

  const isNative = Capacitor.isNativePlatform()

  try {
    if (isNative) {
      const response = await CapacitorHttp.request({
        url: `${API_BASE_URL}/api/subscription/status`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        connectTimeout: 10000,
        readTimeout: 10000,
      })

      if (response.status >= 400) {
        console.error('[Subscription] Status fetch error:', response.status, response.data)
        throw new Error(response.data?.error || `Server error: ${response.status}`)
      }

      return response.data as SubscriptionStatus
    } else {
      const response = await fetch(`${API_BASE_URL}/api/subscription/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Server error: ${response.status}`)
      }

      return await response.json()
    }
  } catch (error) {
    console.error('[Subscription] Failed to fetch status:', error)
    
    // Fallback: read from Supabase directly
    return fetchSubscriptionFromSupabase()
  }
}

/**
 * Fallback: fetch subscription data directly from Supabase
 * Used when the API endpoint is unreachable
 */
async function fetchSubscriptionFromSupabase(): Promise<SubscriptionStatus> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    return {
      tier: 'free',
      isPremium: false,
      status: null,
      provider: null,
      expiresAt: null,
      freeVerificationsUsed: 0,
      freeVerificationsRemaining: FREE_VERIFICATION_LIMIT,
      freeVerificationsLimit: FREE_VERIFICATION_LIMIT,
      purchasedVerifications: 0,
      totalVerificationsRemaining: FREE_VERIFICATION_LIMIT,
    }
  }

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('subscription_tier, subscription_status, subscription_provider, subscription_expires_at, free_verifications_used, free_verifications_reset_at, purchased_verifications')
    .eq('id', user.id)
    .single()

  if (!profile) {
    return {
      tier: 'free',
      isPremium: false,
      status: null,
      provider: null,
      expiresAt: null,
      freeVerificationsUsed: 0,
      freeVerificationsRemaining: FREE_VERIFICATION_LIMIT,
      freeVerificationsLimit: FREE_VERIFICATION_LIMIT,
      purchasedVerifications: 0,
      totalVerificationsRemaining: FREE_VERIFICATION_LIMIT,
    }
  }

  // Expiry safety check
  let effectiveTier = profile.subscription_tier as 'free' | 'premium'
  if (
    effectiveTier === 'premium' &&
    profile.subscription_expires_at &&
    new Date(profile.subscription_expires_at) < new Date() &&
    profile.subscription_status !== 'active'
  ) {
    effectiveTier = 'free'
  }

  const isPremium = effectiveTier === 'premium'
  const freeVerificationsUsed = profile.free_verifications_used ?? 0
  const purchasedVerifications = profile.purchased_verifications ?? 0
  const freeRemaining = Math.max(0, FREE_VERIFICATION_LIMIT - freeVerificationsUsed)

  return {
    tier: effectiveTier,
    isPremium,
    status: profile.subscription_status as SubscriptionStatus['status'],
    provider: profile.subscription_provider as SubscriptionStatus['provider'],
    expiresAt: profile.subscription_expires_at,
    freeVerificationsUsed,
    freeVerificationsRemaining: freeRemaining,
    freeVerificationsLimit: FREE_VERIFICATION_LIMIT,
    purchasedVerifications,
    totalVerificationsRemaining: isPremium ? null : freeRemaining + purchasedVerifications,
  }
}
